/**
 * Local-only IO storage.
 *
 * Uploaded IOs are extracted client-side against the parser service but
 * never persisted to the backend (there's no "save draft" endpoint yet), so
 * the Dashboard has nothing to fetch for them. As a stopgap, extracted
 * records are kept in the browser's localStorage and merged into the
 * Dashboard's table. This is inherently per-browser: records don't sync
 * across devices and are lost if storage is cleared.
 */

const STORAGE_KEY = "longship.uploadedIos";
const MAX_STORED = 200; // keep the list from growing unbounded
const CHANGE_EVENT = "longship:local-ios-changed";

export function loadUploadedIos() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Append newly-extracted IO records, tagging each as a local-only upload.
 *
 * @param {object[]} ioOrders
 * @param {object} [opts]
 * @param {string} [opts.sourceFile] Original filename, for display.
 * @param {string} [opts.sourceDocDataUrl] The original document itself, as a
 *   data: URL, so ReviewPane can show it next to the parsed fields without a
 *   backend round-trip. Caller should skip this for large files — it's
 *   base64-inflated and eats into localStorage's small quota.
 */
export function saveUploadedIos(ioOrders, { sourceFile, sourceDocDataUrl, extractedSections } = {}) {
  if (!Array.isArray(ioOrders) || ioOrders.length === 0) return;
  const existing = loadUploadedIos();
  const now = new Date().toISOString();
  const additions = ioOrders.map((io, idx) => ({
    ...io,
    id: `local-${io.io_number || "unknown"}-${Date.now()}-${idx}`,
    status: "draft",
    source: "upload",
    sourceFile: sourceFile || null,
    sourceDocDataUrl: sourceDocDataUrl || null,
    extractedSections: extractedSections || null,
    createdAt: now,
  }));
  const next = [...additions, ...existing].slice(0, MAX_STORED);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Most likely QuotaExceededError from embedding the original document —
    // retry without the inline previews so the extracted data still saves.
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next.map((r) => ({ ...r, sourceDocDataUrl: null }))));
    } catch {
      // Storage still unavailable — the upload succeeded, it just won't
      // show up on the Dashboard until storage frees up.
    }
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** Look up a single local IO by its `id`, or null if not found. */
export function getUploadedIo(id) {
  return loadUploadedIos().find((r) => r.id === id) || null;
}

/**
 * Replace a local IO's data with `patch` (used when saving field edits made
 * in the Review pane). Preserves id/source/createdAt regardless of what's in
 * `patch` so the record stays identifiable and sorts correctly.
 */
export function updateUploadedIo(id, patch) {
  const existing = loadUploadedIos();
  const next = existing.map((r) =>
    r.id === id ? { ...r, ...patch, id: r.id, source: r.source, createdAt: r.createdAt } : r
  );
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Ignore — nothing to persist.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** Update a single local IO's status in place. */
export function updateUploadedIoStatus(id, status) {
  const existing = loadUploadedIos();
  const next = existing.map((r) => (r.id === id ? { ...r, status } : r));
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Ignore — nothing to persist.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** Remove a single local IO by its `id`. */
export function removeUploadedIo(id) {
  const existing = loadUploadedIos();
  const next = existing.filter((r) => r.id !== id);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Ignore — nothing to persist.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function clearUploadedIos() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore — nothing to clear.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** Subscribe to local-IO changes (same-tab custom event + cross-tab storage event). Returns an unsubscribe fn. */
export function onUploadedIosChanged(callback) {
  const storageListener = (e) => {
    if (!e.key || e.key === STORAGE_KEY) callback();
  };
  window.addEventListener(CHANGE_EVENT, callback);
  window.addEventListener("storage", storageListener);
  return () => {
    window.removeEventListener(CHANGE_EVENT, callback);
    window.removeEventListener("storage", storageListener);
  };
}

export { STORAGE_KEY };
